import * as React from 'react'
import {
  Box,
  Fade,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  keyframes,
  useMediaQuery,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import usePersistedState from '../../hooks/usePersistedState'
import { floatingSurfaceStyles } from '../../theme/floatingSurface'

const MINIMIZED_STORAGE_KEY = 'arbolec_platform_update_notice_minimized'
const NOTICE_MESSAGE = 'Estamos trabajando en actualizaciones importantes en ÁrbolEc.'
const EXPAND_LABEL = 'Ver aviso de actualizaciones'
const MINIMIZE_LABEL = 'Minimizar aviso'
const PULSE_ANIMATION_DURATION = '1.8s'

const pulseRing = keyframes`
  from { transform: scale(1); opacity: 0.7; }
  to { transform: scale(2.6); opacity: 0; }
`

const anchorSx = {
  position: 'fixed',
  top: '50%',
  right: { xs: 12, sm: 20 },
  transform: 'translateY(-50%)',
  zIndex: 'appBar',
  maxWidth: { xs: 'min(280px, calc(100vw - 24px))', sm: 360 },
}

const pulseDotSx = {
  position: 'relative',
  flexShrink: 0,
  width: 8,
  height: 8,
  borderRadius: '50%',
  bgcolor: 'secondary.light',
  color: 'secondary.light',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    border: '2px solid currentColor',
    animation: `${pulseRing} ${PULSE_ANIMATION_DURATION} ease-out infinite`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&::after': { animation: 'none' },
  },
}

const PlatformUpdateNotice = () => {
  const theme = useTheme()
  // En pantallas pequeñas arranca minimizado para no tapar el contenido central (ej. el login).
  const isCompactViewport = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })
  const [isMinimized, setIsMinimized] = usePersistedState(MINIMIZED_STORAGE_KEY, isCompactViewport)

  const handleMinimize = React.useCallback(() => setIsMinimized(true), [setIsMinimized])
  const handleExpand = React.useCallback(() => setIsMinimized(false), [setIsMinimized])

  return (
    <Box sx={anchorSx}>
      <Fade in key={isMinimized ? 'minimized' : 'expanded'}>
        <Box>
          {isMinimized ? (
            <Tooltip title={EXPAND_LABEL} placement="left">
              <IconButton
                aria-label={EXPAND_LABEL}
                onClick={handleExpand}
                sx={theme => ({
                  ...floatingSurfaceStyles(theme),
                  width: 40,
                  height: 40,
                  '&:hover': { bgcolor: theme.palette.primary.main },
                })}>
                <ConstructionRoundedIcon fontSize="small" sx={{ color: 'secondary.light' }} />
                <Box
                  component="span"
                  aria-hidden
                  sx={{ ...pulseDotSx, position: 'absolute', top: 6, right: 6 }}
                />
              </IconButton>
            </Tooltip>
          ) : (
            <Paper
              role="status"
              elevation={0}
              sx={theme => ({
                ...floatingSurfaceStyles(theme),
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                py: 0.75,
                pl: 1.75,
                pr: 0.5,
                borderRadius: 999,
              })}>
              <Box component="span" aria-hidden sx={pulseDotSx} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  lineHeight: 1.35,
                }}>
                {NOTICE_MESSAGE}
              </Typography>
              <Tooltip title={MINIMIZE_LABEL} placement="left">
                <IconButton
                  size="small"
                  aria-label={MINIMIZE_LABEL}
                  onClick={handleMinimize}
                  sx={theme => ({
                    color: 'inherit',
                    opacity: 0.8,
                    '&:hover': { opacity: 1, bgcolor: alpha(theme.palette.common.white, 0.12) },
                  })}>
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          )}
        </Box>
      </Fade>
    </Box>
  )
}

export default PlatformUpdateNotice
